import { Anchor, Badge, Box, Button, Card, Flex, Group, ScrollArea, Text, Title } from "@mantine/core";
import { IStatbox } from "@mctypes/frontend.types";
import type { IIssueApi } from "@schemas/issueSchema";
import type { IScoreMatrixConfig } from "@mctypes/issue.types";
import type { ColDef } from "ag-grid-community";
import React, { useMemo, useState } from "react";
import { OptionBadge } from "../../../../../../components";
import ViewEditTable from "../../../../../../components/ViewEditTable/ViewEditTable";
import { useAppDispatch, useAppSelector } from "../../../../../../hooks";
import LocalErrorBoundary from "../../../../../../util/LocalErrorBoundary";
import { StatsBox } from "../../../StatsBox";
import { ISSUE_STATES_OPTIONS } from "../../../issueConstants";
import { selectCategoryMatrices, selectCategoryMatricesAfterTiltak } from "../../../issueRisikoSelectors";
import { selectIssuesWithTypeISSUE } from "../../../issueSelectors";
import { thunkOpenIssueModalById } from "../../../issueSlice";
import { convertValueAndColorToMultiDisplayColor, deduplicateMatrices } from "../../../issueUtils";
import { selectHttpIssueBoard } from "../../../redux/selectors/issueRtkSelectors";
import "./RisikoMatrix.css";

const RisikoDashboard: React.FC = () => {
  const [showAckumulated, setShowAckumulated] = useState(false);
  const [selectedIssues, setSelectedIssues] = useState<IIssueApi[]>([]);

  const issues = useAppSelector(selectIssuesWithTypeISSUE);
  const issueBoard = useAppSelector(selectHttpIssueBoard);

  const boxes: IStatbox[] = [
    {
      icon: "fa-book",
      value: issues.filter((x) => x.archived !== true).length,
      text: `Registrert`,
      classColor: "icon-primary",
    },
    {
      icon: "fa-folder-open",
      value: issues.filter((x) => x.status === "Opprettet" || x.status === "I prosess").length,
      text: `Åpne`,
      classColor: "icon-primary",
    },
    {
      icon: "fa-check",
      value: issues.filter((x) => {
        if (x.dates.completed == null) return false;
        const completedDate = new Date(x.dates.completed);
        const now = new Date();
        const twoWeeksAgo = new Date(now.setDate(now.getDate() - 14));
        return completedDate >= twoWeeksAgo;
      }).length,
      text: `Lukket siste 14 dager`,
      classColor: "icon-success",
    },
    {
      icon: "fa-calendar",
      value: issues.filter((x) => {
        if (x.dates.due == null) return false;
        const dueDate = new Date(x.dates.due);
        const now = new Date();
        const twoWeeksFromNow = new Date(now.setDate(now.getDate() + 14));
        return dueDate <= twoWeeksFromNow;
      }).length,
      text: `Tidsfrist neste 14 dager`,
      classColor: "icon-danger",
    },
  ];

  return (
    // Gör HELA sidan scrollbar (föräldrar kan vara h:100%)
    <ScrollArea h="100%" w="100%">
      <Card withBorder p="md" w="100%">
        <Group justify="space-between" align="center" mb="xs">
          <Title order={4}>Risikoer</Title>
          <Badge variant="filled" radius="sm" color="mc-blue">
            {issues.length}
          </Badge>
        </Group>

        {/* Statboxes */}
        <Group justify="flex-start" wrap="nowrap" gap="sm">
          {boxes.map((stat, idx) => (
            <StatsBox key={idx} stat={stat} />
          ))}
        </Group>

        {/* Innehåll: matriser + tabell. Hela kortet kan bli högt; ScrollArea runt allt gör att sidan scrollar */}
        <Flex gap="md" mt="md">
          {/* Vänster: Kategorier */}
          <Card withBorder p="md" flex={1} style={{ minWidth: 0 }}>
            <Group justify="space-between" align="center" mb="xs">
              <Title order={4}>Kategorier</Title>
              <Button
                size="xs"
                variant={showAckumulated ? "filled" : "outline"}
                onClick={() => setShowAckumulated((v) => !v)}
              >
                Akkumulert
              </Button>
            </Group>

            <Text size="sm" c="dimmed" mb="xs">
              <i className="fa fa-info-circle fa-fw" /> Klikk på en celle for å se aktuelle risikoer
            </Text>

            <LocalErrorBoundary>
              {issueBoard.risikoSection != null && (
                <RisikoMatrices
                  ackumulated={showAckumulated}
                  onCellClick={setSelectedIssues}
                  showAfterTiltak={issueBoard.risikoSection.enableProbabilityAfterTiltak === true}
                />
              )}
            </LocalErrorBoundary>
          </Card>

          {/* Höger: Risikoer lista */}
          <Card withBorder p="md" flex={1} style={{ minWidth: 0 }}>
            <Box w="100%">
              <RisikoTable issues={selectedIssues.length > 0 ? selectedIssues : issues} />
            </Box>
          </Card>
        </Flex>
      </Card>
    </ScrollArea>
  );
};

export default RisikoDashboard;

const RisikoMatrices: React.FC<{
  ackumulated: boolean;
  onCellClick: (issues: IIssueApi[]) => void;
  showAfterTiltak: boolean;
}> = ({ ackumulated, onCellClick, showAfterTiltak }) => {
  const [selectedCell, setSelectedCell] = useState<string | null>(null);

  const categoryMatrices = useAppSelector(selectCategoryMatrices);
  const categoryMatricesAfterTiltak = useAppSelector(selectCategoryMatricesAfterTiltak);
  const issueBoard = useAppSelector(selectHttpIssueBoard);

  const consequenceOnXAxis = issueBoard.risikoSection?.consequenceOnXAxis === true;
  const beforeMatrices = useMemo(
    () => getDisplayMatrices(categoryMatrices, ackumulated, consequenceOnXAxis),
    [categoryMatrices, ackumulated, consequenceOnXAxis],
  );
  const afterMatrices = useMemo(
    () => getDisplayMatrices(categoryMatricesAfterTiltak, ackumulated, consequenceOnXAxis),
    [categoryMatricesAfterTiltak, ackumulated, consequenceOnXAxis],
  );
  const probabilities = Object.values(issueBoard.risikoSection?.probabilites ?? {})
    .filter((probability) => probability.isActive)
    .map((probability) => probability.name);
  const categories = Object.values(issueBoard.risikoSection?.evaluationCategoriesConfig ?? {}).filter(
    (category) => category.isActive,
  );

  return (
    <Flex direction="column" gap="lg">
      {beforeMatrices.map((beforeMatrix, index) => {
        const sourceCategoryIndexes = beforeMatrix.sourceCategoryIndexes ?? [index];
        const afterMatrix = afterMatrices.find(
          (matrix) => JSON.stringify(matrix.sourceCategoryIndexes ?? []) === JSON.stringify(sourceCategoryIndexes),
        );

        return (
          <Card key={sourceCategoryIndexes.join("-")} withBorder p="sm">
            <Title order={5} mb="sm">
              {beforeMatrix.name}
            </Title>
            {showAfterTiltak ? (
              <Flex direction="column" gap="lg">
                <Box>
                  <Text fw={600} size="sm" mb="xs">
                    Før tiltak
                  </Text>
                  <RisikoMatrix
                    categoryMatrix={beforeMatrix}
                    matrixKey={`before-${sourceCategoryIndexes.join("-")}`}
                    sourceCategoryIndexes={sourceCategoryIndexes}
                    categories={categories}
                    probabilities={probabilities}
                    consequenceOnXAxis={consequenceOnXAxis}
                    selectedCell={selectedCell}
                    setSelectedCell={setSelectedCell}
                    onCellClick={onCellClick}
                  />
                </Box>
                <Box>
                  <Text fw={600} size="sm" mb="xs">
                    Etter tiltak
                  </Text>
                  {afterMatrix != null ? (
                    <RisikoMatrix
                      categoryMatrix={afterMatrix}
                      matrixKey={`after-${sourceCategoryIndexes.join("-")}`}
                      sourceCategoryIndexes={sourceCategoryIndexes}
                      categories={categories}
                      probabilities={probabilities}
                      consequenceOnXAxis={consequenceOnXAxis}
                      selectedCell={selectedCell}
                      setSelectedCell={setSelectedCell}
                      onCellClick={onCellClick}
                    />
                  ) : (
                    <Text size="sm" c="dimmed">Ingen matrise tilgjengelig.</Text>
                  )}
                </Box>
              </Flex>
            ) : (
              <RisikoMatrix
                categoryMatrix={beforeMatrix}
                matrixKey={`before-${sourceCategoryIndexes.join("-")}`}
                sourceCategoryIndexes={sourceCategoryIndexes}
                categories={categories}
                probabilities={probabilities}
                consequenceOnXAxis={consequenceOnXAxis}
                selectedCell={selectedCell}
                setSelectedCell={setSelectedCell}
                onCellClick={onCellClick}
              />
            )}
          </Card>
        );
      })}
    </Flex>
  );
};

const getDisplayMatrices = (matrices: IScoreMatrixConfig[], ackumulated: boolean, consequenceOnXAxis: boolean) => {
  const matricesWithSourceIndexes = matrices.map((matrix, index) => ({
    ...matrix,
    sourceCategoryIndexes: matrix.sourceCategoryIndexes ?? [index],
  }));
  const matricesToDisplay = ackumulated ? deduplicateMatrices(matricesWithSourceIndexes) : matricesWithSourceIndexes;
  if (!consequenceOnXAxis) return matricesToDisplay;

  return matricesToDisplay.map((categoryMatrix) => ({
    ...categoryMatrix,
    matrix:
      categoryMatrix.matrix[0]
        ?.map((_, columnIndex) => categoryMatrix.matrix.map((row) => row[columnIndex]))
        .reverse() ?? [],
  }));
};

const RisikoMatrix: React.FC<{
  categoryMatrix: IScoreMatrixConfig;
  matrixKey: string;
  sourceCategoryIndexes: number[];
  categories: ReturnType<typeof Object.values>;
  probabilities: string[];
  consequenceOnXAxis: boolean;
  selectedCell: string | null;
  setSelectedCell: React.Dispatch<React.SetStateAction<string | null>>;
  onCellClick: (issues: IIssueApi[]) => void;
}> = ({ categoryMatrix, matrixKey, sourceCategoryIndexes, categories, probabilities, consequenceOnXAxis, selectedCell, setSelectedCell, onCellClick }) => {
  const consequenceAxisValues = sourceCategoryIndexes.map((categoryIndex) =>
    Object.values((categories as any)[categoryIndex]?.consequences ?? {})
      .filter((consequence: any) => consequence.isActive)
      .map((consequence: any) => consequence.name),
  );
  const consequences = consequenceAxisValues[0] ?? [];
  const hasMatchingConsequenceAxes = consequenceAxisValues.every(
    (axisValues) => JSON.stringify(axisValues) === JSON.stringify(consequences),
  );
  const xAxisValues = consequenceOnXAxis && !hasMatchingConsequenceAxes ? [] : consequenceOnXAxis ? consequences : probabilities;
  const yAxisValues = consequenceOnXAxis ? [...probabilities].reverse() : hasMatchingConsequenceAxes ? consequences : [];

  return (
    <>
      {!hasMatchingConsequenceAxes && (
        <Text size="xs" c="dimmed">Konsekvensetiketter vises ikke for en akkumulert matrise med ulike kategorier.</Text>
      )}
      <div className="matrix-with-axis-labels">
        <div className="matrix-y-axis">
          {yAxisValues.map((value, valueIndex) => <div className="matrix-axis-label" key={valueIndex}>{value}</div>)}
        </div>
        <div className="matrix-with-x-axis">
          <div className="matrix-x-axis">
            {xAxisValues.map((value, valueIndex) => <div className="matrix-axis-label" key={valueIndex}>{value}</div>)}
          </div>
          <div className="matrix-container">
            {categoryMatrix.matrix.map((row, rowIndex) => (
              <div className="matrix-row" key={rowIndex}>
                {row.map((cell, cellIndex) => {
                  const cellKey = `${matrixKey}-${rowIndex}-${cellIndex}`;
                  return (
                    <div key={cellIndex} className={`matrix-cell${cell.score > 0 ? " pointer" : ""}${selectedCell === cellKey ? " selected-matrix-cell" : ""}`} style={{ backgroundColor: cell.color }} onClick={() => {
                      if (cell.score === 0) return;
                      if (selectedCell === cellKey) { setSelectedCell(null); onCellClick([]); return; }
                      setSelectedCell(cellKey); onCellClick(cell.issues);
                    }}>
                      {cell.score > 0 && <span className="score">{cell.score}</span>}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export const RisikoTable: React.FC<{ issues: IIssueApi[] }> = ({ issues }) => {
  const dispatch = useAppDispatch();

  const columns = useMemo<ColDef<IIssueApi>[]>(
    () => [
      {
        headerName: "ID",
        width: 80,
        field: "id",
        cellRenderer: (params) => (
          <Anchor
            component="button"
            onClick={() => dispatch(thunkOpenIssueModalById(params.data._id))}
            variant="subtle"
          >
            {params.value}
          </Anchor>
        ),
      },
      { minWidth: 120, flex: 1, headerName: "Tittel", field: "title" },
      { minWidth: 120, flex: 1, headerName: "Beskrivelse", field: "description" },
      {
        width: 120,
        headerName: "Opprettet",
        field: "createdAt",
        cellRenderer: (params) => new Date(params.value).toLocaleDateString(),
      },
      {
        width: 120,
        headerName: "Status",
        field: "status",
        cellRenderer: (params) => (
          <OptionBadge
            activeOption={params.value}
            options={convertValueAndColorToMultiDisplayColor(ISSUE_STATES_OPTIONS)}
          />
        ),
      },
    ],
    [dispatch],
  );

  const deduplicatedIssues = useMemo(
    () => issues.filter((issue, index, self) => self.findIndex((t) => t._id === issue._id) === index),
    [issues],
  );

  return (
    <ViewEditTable
      gridOptions={{
        // AutoHeight gör att tabellen växer och sidan (ScrollArea) blir scrollbar
        domLayout: "autoHeight",
        pagination: true,
        paginationPageSize: 20,
      }}
      data={deduplicatedIssues}
      columns={columns}
      onSave={() => {}}
      newItemTemplateObject={null}
    />
  );
};
